"use client"

import React, { useEffect, useState, useContext } from "react"
import { Link, useNavigate } from "react-router-dom"
import axios from "axios"
import { motion } from "framer-motion"
import { Trophy, Download, AlertCircle, Clock, CheckCircle2, FileText } from "lucide-react"
import { CircuitoContext } from "../../context/CircuitoContext"
import { UserContext } from "../../context/UserContext"
import { DomainContext } from "./LayoutCircuito"
import "../../styles/circuitoDesignSystem.css"

const API = process.env.REACT_APP_API_URL || "https://api.ticketdovaqueiro.com/api"

export default function MinhaAssociacaoCircuito() {
  const { circuito, loadingCircuito } = useContext(CircuitoContext)
  const { user } = useContext(UserContext)
  const { basePath } = useContext(DomainContext)
  const navigate = useNavigate()

  const [inscricoes, setInscricoes] = useState([])
  const [loading, setLoading] = useState(true)
  const [baixandoPdf, setBaixandoPdf] = useState(null)

  useEffect(() => {
    if (!user) {
      navigate(`${basePath}/inscrever-se/acesso`)
      return
    }

    if (circuito && circuito._id) {
      buscarMinhasInscricoes()
    }
  }, [user, circuito, navigate, basePath])

  const buscarMinhasInscricoes = async () => {
    try {
      const token = localStorage.getItem("token")
      const res = await axios.get(`${API}/circuitos/${circuito._id}/minhas-inscricoes`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setInscricoes(res.data)
    } catch (err) {
      console.error("Erro ao buscar associações")
    } finally {
      setLoading(false)
    }
  }

  const baixarComprovante = async (pedidoId, nomeVaqueiro) => {
    if (!pedidoId) return;
    setBaixandoPdf(pedidoId);
    try {
      const res = await axios.get(`${API}/circuitos/pedidos/${pedidoId}/comprovante`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Comprovante-${circuito?.nome.replace(/\s+/g, '')}-${nomeVaqueiro.replace(/\s+/g, '')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      alert("Erro ao baixar o comprovante. Tente novamente.");
    } finally {
      setBaixandoPdf(null);
    }
  };

  if (loadingCircuito || loading) {
    return <div className="circuito-shell"><div className="loading-spinner" style={{ margin: "100px auto" }}></div></div>;
  }

  return (
    <div className="circuito-shell" style={{ padding: "40px 16px", minHeight: "70vh" }}>
      <div style={{ maxWidth: 1000, margin: "0 auto" }}>
        
        <div style={{ marginBottom: "32px" }}>
          <h1 style={{ fontSize: "28px", fontWeight: 800, color: "#fff", marginBottom: 8 }}>
            Minhas <span style={{ color: "var(--c-accent)" }}>Associações</span>
          </h1>
          <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "15px", margin: 0 }}>
            Histórico de associações atreladas à sua conta no <strong>{circuito?.nome}</strong>.
          </p>
        </div>

        {inscricoes.length === 0 ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} style={{ background: "rgba(255,255,255,0.02)", border: "1px dashed rgba(255,255,255,0.1)", borderRadius: "16px", padding: "60px 20px", textAlign: "center" }}>
            <div style={{ display: "inline-flex", background: "rgba(255,255,255,0.05)", padding: "20px", borderRadius: "50%", marginBottom: "20px" }}>
              <Trophy size={48} color="rgba(255,255,255,0.2)" />
            </div>
            <h2 style={{ color: "#fff", fontSize: "20px", marginBottom: "12px" }}>Nenhuma associação encontrada</h2>
            <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "15px", marginBottom: "32px", maxWidth: "400px", margin: "0 auto 32px" }}>
              Você ainda não possui inscrições neste circuito. Garanta sua vaga, acumule pontos e concorra ao título de Campeão dos Campeões.
            </p>
            <Link to={`${basePath}/inscrever-se`} style={{ textDecoration: "none" }}>
              <button className="c-btn c-btn--primary" style={{ height: "50px", padding: "0 32px", fontSize: "15px", fontWeight: 700, borderRadius: "25px" }}>
                Fazer Associação Agora
              </button>
            </Link>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} style={{ background: "var(--c-card)", border: "var(--card-border)", borderRadius: "var(--card-radius)", overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", minWidth: "800px" }}>
                <thead>
                  <tr style={{ background: "rgba(0,0,0,0.3)", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                    <th style={{ padding: "16px 20px", color: "rgba(255,255,255,0.5)", fontSize: "12px", textTransform: "uppercase", fontWeight: 700 }}>Vaqueiro</th>
                    <th style={{ padding: "16px 20px", color: "rgba(255,255,255,0.5)", fontSize: "12px", textTransform: "uppercase", fontWeight: 700 }}>Categoria</th>
                    <th style={{ padding: "16px 20px", color: "rgba(255,255,255,0.5)", fontSize: "12px", textTransform: "uppercase", fontWeight: 700 }}>Representação</th>
                    <th style={{ padding: "16px 20px", color: "rgba(255,255,255,0.5)", fontSize: "12px", textTransform: "uppercase", fontWeight: 700, textAlign: "center" }}>Status</th>
                    <th style={{ padding: "16px 20px", color: "rgba(255,255,255,0.5)", fontSize: "12px", textTransform: "uppercase", fontWeight: 700, textAlign: "center" }}>Comprovante</th>
                  </tr>
                </thead>
                <tbody>
                  {inscricoes.map((insc, idx) => {
                    const isPago = insc.statusPagamento === "pago";
                    const isPendente = insc.statusPagamento === "pendente";
                    
                    return (
                      <tr key={insc._id} style={{ borderBottom: idx === inscricoes.length - 1 ? "none" : "1px solid rgba(255,255,255,0.02)", transition: "background 0.2s" }} onMouseOver={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.02)"} onMouseOut={(e) => e.currentTarget.style.background = "transparent"}>
                        
                        {/* Nome do Vaqueiro e Data */}
                        <td style={{ padding: "16px 20px" }}>
                          <strong style={{ color: "#fff", fontSize: "14px", display: "block" }}>{insc.nomeVaqueiro.toUpperCase()}</strong>
                          <span style={{ color: "rgba(255,255,255,0.4)", fontSize: "12px" }}>{new Date(insc.createdAt).toLocaleDateString('pt-BR')}</span>
                        </td>
                        
                        {/* Categoria e Cavalo */}
                        <td style={{ padding: "16px 20px" }}>
                          <span style={{ color: "var(--c-accent)", fontSize: "14px", fontWeight: 600, display: "block" }}>{insc.categoria.toUpperCase()}</span>
                          {insc.cavalo && <span style={{ color: "rgba(255,255,255,0.6)", fontSize: "11px" }}>🐴 {insc.cavalo}</span>}
                        </td>

                        {/* Equipe / Representação */}
                        <td style={{ padding: "16px 20px", color: "rgba(255,255,255,0.8)", fontSize: "14px" }}>
                          {insc.equipe}
                        </td>

                        {/* Status do Pagamento */}
                        <td style={{ padding: "16px 20px", textAlign: "center" }}>
                          <div style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "4px 10px", borderRadius: "12px", fontSize: "11px", fontWeight: 800, background: isPago ? "rgba(16, 185, 129, 0.15)" : isPendente ? "rgba(245, 158, 11, 0.15)" : "rgba(239, 68, 68, 0.15)", color: isPago ? "#10b981" : isPendente ? "#f59e0b" : "#ef4444", textTransform: "uppercase" }}>
                            {isPago ? <CheckCircle2 size={12} /> : isPendente ? <Clock size={12} /> : <AlertCircle size={12} />}
                            {insc.statusPagamento}
                          </div>
                        </td>

                        {/* Ação (Baixar PDF) */}
                        <td style={{ padding: "16px 20px", textAlign: "center" }}>
                          {isPago ? (
                            <button 
                              onClick={() => baixarComprovante(insc.pedidoId?._id, insc.nomeVaqueiro)}
                              disabled={baixandoPdf === insc.pedidoId?._id}
                              title="Baixar Comprovante PDF"
                              style={{ 
                                background: "rgba(212, 175, 55, 0.1)", color: "var(--c-accent)", border: "1px solid rgba(212, 175, 55, 0.3)",
                                width: "36px", height: "36px", borderRadius: "8px", display: "inline-flex", alignItems: "center", justifyContent: "center",
                                cursor: baixandoPdf === insc.pedidoId?._id ? "wait" : "pointer", transition: "all 0.2s", opacity: baixandoPdf === insc.pedidoId?._id ? 0.5 : 1
                              }}
                              onMouseOver={(e) => !baixandoPdf && (e.currentTarget.style.background = "var(--c-accent)", e.currentTarget.style.color = "#000")}
                              onMouseOut={(e) => !baixandoPdf && (e.currentTarget.style.background = "rgba(212, 175, 55, 0.1)", e.currentTarget.style.color = "var(--c-accent)")}
                            >
                              {baixandoPdf === insc.pedidoId?._id ? <Clock size={18} /> : <FileText size={18} />}
                            </button>
                          ) : (
                            <span style={{ color: "rgba(255,255,255,0.3)", fontSize: "12px", fontStyle: "italic" }}>-</span>
                          )}
                        </td>

                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  )
}